import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import toast from 'react-hot-toast';

const AdminIssues = () => {
  const [issues, setIssues] = useState({ failedPayments: [], lateUploads: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('failedPayments');
  const { api } = useApi();

  const fetchIssues = useCallback(async () => {
    try {
      const response = await api.get('/admin/issues');
      setIssues(response.data.data);
    } catch (error) {
      toast.error('Failed to load issues');
      console.error('Issues error:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const handleResolveLateUpload = async (orderId) => {
    try {
      // This would typically update the order status or send a reminder
      toast.success('Reminder sent to band director');
      // Refresh the issues list
      fetchIssues();
    } catch (error) {
      toast.error('Failed to send reminder');
      console.error('Resolve error:', error);
    }
  };

  const handleRetryPayment = async (transactionId) => {
    try {
      // This would typically retry the payment processing
      toast.success('Payment retry initiated');
      // Refresh the issues list
      fetchIssues();
    } catch (error) {
      toast.error('Failed to retry payment');
      console.error('Retry error:', error);
    }
  };

  const handleExportIssues = () => {
    const csvContent = [
      ['Issue Type', 'Order ID', 'Band', 'School', 'Customer', 'Amount', 'Days Late', 'Date'],
      ...issues.failedPayments.map(payment => [
        'Failed Payment',
        payment.orderId,
        payment.bandName,
        payment.schoolName,
        payment.customerName,
        `$${payment.amount}`,
        '',
        new Date(payment.createdAt).toLocaleDateString()
      ]),
      ...issues.lateUploads.map(upload => [
        'Late Upload',
        upload.id,
        upload.bandName,
        upload.schoolName,
        upload.customerName,
        `$${upload.price}`,
        upload.daysLate,
        new Date(upload.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bandcam-issues-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Issues exported successfully');
  };

  const handleLogout = () => {
    localStorage.removeItem('bandcam_admin_token');
    delete api.defaults.headers.common['Authorization'];
    window.location.href = '/admin/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-gray-900">BandCam Admin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleExportIssues}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Export Issues
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link
              to="/admin/dashboard"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Dashboard
            </Link>
            <Link
              to="/admin/bands"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Bands
            </Link>
            <Link
              to="/admin/orders"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Orders
            </Link>
            <Link
              to="/admin/schools"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Schools
            </Link>
            <Link
              to="/admin/issues"
              className="text-indigo-600 border-b-2 border-indigo-600 py-2 px-1 text-sm font-medium"
            >
              Issues
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Issue Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Failed Payments</dt>
                    <dd className="text-lg font-medium text-gray-900">{issues.failedPayments.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Late Uploads</dt>
                    <dd className="text-lg font-medium text-gray-900">{issues.lateUploads.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('failedPayments')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'failedPayments'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Failed Payments ({issues.failedPayments.length})
              </button>
              <button
                onClick={() => setActiveTab('lateUploads')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'lateUploads'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Late Uploads ({issues.lateUploads.length})
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'failedPayments' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Failed Payments</h3>
                {issues.failedPayments.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Band</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occasion</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {issues.failedPayments.map((payment) => (
                          <tr key={payment.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {payment.orderId.slice(0, 8)}...
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-medium">{payment.bandName}</div>
                                <div className="text-gray-500">{payment.schoolName}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-medium">{payment.customerName}</div>
                                <div className="text-gray-500">{payment.customerEmail}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {payment.recipientName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {payment.occasion}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              ${payment.amount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(payment.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => handleRetryPayment(payment.id)}
                                className="text-indigo-600 hover:text-indigo-900 mr-3"
                              >
                                Retry Payment
                              </button>
                              <a
                                href={`mailto:${payment.customerEmail}?subject=Payment Issue - Order ${payment.orderId.slice(0, 8)}&body=Hi ${payment.customerName},%0D%0A%0D%0AWe noticed there was an issue with your payment for the order ${payment.orderId.slice(0, 8)}. Please contact us to resolve this.%0D%0A%0D%0ABest regards,%0D%0ABandCam Team`}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Contact Customer
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No failed payments</h3>
                    <p className="mt-1 text-sm text-gray-500">All payments are processing successfully.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'lateUploads' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Late Uploads</h3>
                {issues.lateUploads.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Band</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Director</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occasion</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Late</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {issues.lateUploads.map((upload) => (
                          <tr key={upload.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {upload.id.slice(0, 8)}...
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-medium">{upload.bandName}</div>
                                <div className="text-gray-500">{upload.schoolName}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-medium">{upload.directorName}</div>
                                <div className="text-gray-500">{upload.directorEmail}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {upload.customerName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {upload.recipientName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {upload.occasion}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              ${upload.price}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                upload.daysLate > 14 ? 'bg-red-100 text-red-800' :
                                upload.daysLate > 7 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-orange-100 text-orange-800'
                              }`}>
                                {upload.daysLate} days
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => handleResolveLateUpload(upload.id)}
                                className="text-indigo-600 hover:text-indigo-900 mr-3"
                              >
                                Send Reminder
                              </button>
                              <a
                                href={`mailto:${upload.directorEmail}?subject=Late Upload Reminder - Order ${upload.id.slice(0, 8)}&body=Hi ${upload.directorName},%0D%0A%0D%0AThis is a reminder that the video for order ${upload.id.slice(0, 8)} is overdue by ${upload.daysLate} days. Please upload the video as soon as possible.%0D%0A%0D%0ABest regards,%0D%0ABandCam Team`}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Email Director
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No late uploads</h3>
                    <p className="mt-1 text-sm text-gray-500">All videos are being uploaded on time.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminIssues; 